import { defineRule, configure } from "vee-validate";
import { localize, setLocale } from "@vee-validate/i18n";
import { getSectionByPrefCityGroupedByStepA } from "@/common/graphql/sectionByPrefCity";
import { required, max, min, numeric, regex, length } from "@vee-validate/rules";
import ja from "@vee-validate/i18n/dist/locale/ja.json";
import type {
  RegularDeliveryCreateInput,
  RegularDeliveryUpdateInput
} from "@/generated/graphql";
import {
  OperationInput,
  GetBlockByPrefCitySectionDocument,
  GetBlockByPrefCitySectionQuery,
  GetBlockByZipCodeDocument,
  GetBlockByZipCodeQuery,
  ListDeliveryAreaSectionUnassignedDocument,
  ListDeliveryAreaSectionUnassignedQuery,
  GetShopQuery,
  GetShopDocument,
  GetChainDocument,
  GetChainQuery,
  GetMemberQuery,
  GetMemberDocument,
  CreateMemberDocument,
  CreateMemberMutation,
  CreateMemberMutationVariables,
  MemberCreateInput,
  UpdateMemberDocument,
  UpdateMemberMutation,
  UpdateMemberMutationVariables,
  MemberUpdateInput,
  GetChainPaymentMethodDocument,
  GetChainPaymentMethodQuery,
  DeliverableAreaDeliveryIdInput,
  ListDeliverableAreaDeliveryIdQuery,
  ListDeliverableAreaDeliveryIdDocument,
  CreateRegularDeliveryMutation,
  CreateRegularDeliveryMutationVariables,
  CreateRegularDeliveryDocument,
  ListDeliveryTimeQuery,
  ListDeliveryTimeQueryVariables,
  ListDeliveryTimeDocument,
  DeliveryTimeListInput,
  ListDeliveryAreaQuery,
  ListDeliveryAreaQueryVariables,
  ListDeliveryAreaDocument,
  GetRegularDeliveryQuery,
  GetRegularDeliveryDocument,
  UpdateRegularDeliveryMutation,
  UpdateRegularDeliveryMutationVariables,
  UpdateRegularDeliveryDocument,
  DeleteRegularDeliveryMutation,
  DeleteRegularDeliveryMutationVariables,
  DeleteRegularDeliveryDocument,
  GetZipCodeByPrefCitySectionQuery,
  GetZipCodeByPrefCitySectionDocument,
  GetZipCodeByPrefCitySectionBlockQuery,
  GetZipCodeByPrefCitySectionBlockDocument,
  GetPrefByChainQuery,
  GetPrefByChainDocument,
  GetRegularDeliveryQueryVariables,
} from "@/generated/graphql";
import regularDeliveryStore from "@/store/regularDeliveryStore";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import { ApolloQueryResult, FetchResult } from "@apollo/client/core";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { getCityByPrefGroupedByStepA } from "@/common/graphql/cityByPref";
import { getChainInfo, getChainPaymentMethod } from "@/common/graphql/chain";
import { getWordList } from "@/common/graphql/expression";
import { GeneralTypeList } from "@/common/constant";
import { routerPath } from "@/common/router";
import { useRouter } from "vue-router";
import { reactive, ref, onMounted, nextTick, computed } from "vue";
import type { DeliveryAvailabilityData, ListData } from "../types";
import { useRegularDeliveryRegisterActions } from "./useRegularDeliveryRegisterActions";
import { useRegularDeliveryRegisterSubmit } from "./useRegularDeliveryRegisterSubmit";
import { REGULAR_DELIVERY_REGISTER_FILE_NAME } from "../constants";

/**
 * 定期配送登録画面の状態と振る舞いを提供する composable。
 */
defineRule("required", required);
defineRule("max", max);
defineRule("min", min);
defineRule("numeric", numeric);
defineRule("regex", regex);
defineRule("length", length);
defineRule("time_format", (value: unknown): true | string => {
  if (value === null || value === undefined || value === "") {
    return true;
  }
  const text = String(value);
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(text)
    || "時刻はhh:mm形式で入力してください。";
});
configure({
  generateMessage: localize({
    ja,
  }),
});
setLocale("ja");

/**
 * useRegularDeliveryRegister の画面ロジックを提供する composable。
 *
 * @returns 画面ロジックで利用する状態・関数
 */
export const useRegularDeliveryRegister = () => {
// === State variables ===
const router = useRouter();
const errdialog = ref(false); //エラーダイアログ
const errMessage = ref(""); //エラーメッセージ
const updateMode = ref(false);
const deleteMode = ref(false);
const areaDialog = ref(regularDeliveryStore.areaDialog);
const overlay = ref(false); //Loading
const wordList = reactive<Record<string, string>>({});
const wordListLoaded = ref(false);
const dummy_payment_amount = ref("");
const displayConfirmDialog = ref(false);
const displayCompletedDialog = ref(false);

const memberCreateInput = reactive<MemberCreateInput>({
  address1: "",
  address2: "",
  address3: "",
  chain_code: "",
  customer_code: "",
  name1: "",
  name2: "",
  name_kana1: "",
  name_kana2: "",
  notes: "",
  phone: "",
  shop_code: "",
  zip_code: "",
  house_card: "",
});

const memberUpdateInput = reactive<MemberUpdateInput>({
  customer_code: "",
  chain_code: "",
  address1: "",
  address2: "",
  address3: "",
  member_id: "",
  name1: "",
  name2: "",
  name_kana1: "",
  name_kana2: "",
  search_kana: "",
  notes: "",
  phone: "",
  shop_code: "",
  zip_code: "",
  house_card: "",
});

const createMemberMutation = ref<CreateMemberMutation | null | undefined>(null);
const updateMemberMutation = ref<UpdateMemberMutation | null | undefined>(null);

const zipAddressList = ref<
  (
    | {
        __typename?: "ZipAddressName" | undefined;
        pref_name: string;
        city_name: string;
        city_kana: string;
        section_name: string;
        section_kana: string;
        block_name?: string | null | undefined;
      }
    | null
    | undefined
  )[]
    | null
    | undefined
>(undefined);

const zipCodeFromPref = ref("");
const cityBlockList = ref<
  | {
      __typename?: "Kana_Step_A" | undefined;
      line_a?: (string | null | undefined)[] | null | undefined;
      line_ka?: (string | null | undefined)[] | null | undefined;
      line_sa?: (string | null | undefined)[] | null | undefined;
      line_ta?: (string | null | undefined)[] | null | undefined;
      line_na?: (string | null | undefined)[] | null | undefined;
      line_ha?: (string | null | undefined)[] | null | undefined;
      line_ma?: (string | null | undefined)[] | null | undefined;
      line_ya?: (string | null | undefined)[] | null | undefined;
      line_ra?: (string | null | undefined)[] | null | undefined;
      line_wa?: (string | null | undefined)[] | null | undefined;
      line_other?: (string | null | undefined)[] | null | undefined;
    }
  | null
  | undefined
>(null);
const originalAddress = ref("");
const originalSectionAddress = ref("");
const originalCityAddress = ref("");
const originalBlockName = ref("");
const sectionBlockList = ref<
  | {
      __typename?: "Kana_Step_A" | undefined;
      line_a?: (string | null | undefined)[] | null | undefined;
      line_ka?: (string | null | undefined)[] | null | undefined;
      line_sa?: (string | null | undefined)[] | null | undefined;
      line_ta?: (string | null | undefined)[] | null | undefined;
      line_na?: (string | null | undefined)[] | null | undefined;
      line_ha?: (string | null | undefined)[] | null | undefined;
      line_ma?: (string | null | undefined)[] | null | undefined;
      line_ya?: (string | null | undefined)[] | null | undefined;
      line_ra?: (string | null | undefined)[] | null | undefined;
      line_wa?: (string | null | undefined)[] | null | undefined;
      line_other?: (string | null | undefined)[] | null | undefined;
    }
  | null
  | undefined
>(null);
const blockList = ref<Array<string | null | undefined>>([]);
const addressBlockList = ref<Array<string | null | undefined>>([]);
// 配達対象外区画リスト（master準拠）
const unassignedBlockList = ref<Array<string | null | undefined>>([]);
// 配達対象外区画存在メッセージ取得＆置換（master準拠）
const areaNotSelectLabel = computed((): string => {
  const unassignedBlocks = unassignedBlockList.value.join(", ");
  return wordList.lbl_area_not_select.replace("{{s}}", unassignedBlocks);
});
const prefListItems = ref<(string | null | undefined)[] | null | undefined>(undefined);

/**
 * カンマ処理
 */
const commmaMoney = async (value: string): Promise<void> => {
  dummy_payment_amount.value = String(value).replace(
    /(\d)(?=(\d\d\d)+(?!\d))/g,
    "$1,"
  );
};

/**
 * カンマ削除処理
 */
const removeComma = async (value: string): Promise<string> => {
  return String(value).replace(/,/g, "").replace(/¥/g, "");
};

/**
 * 金額フォーカスON
 */
const onFocus = async (value: string): Promise<void> => {
  dummy_payment_amount.value = await removeComma(value);
};

/**
 * 金額フォーカスアウト
 */
const onBlur = async (value: string): Promise<void> => {
  //カンマ削除
  value = await removeComma(value);

  //数値チェック
  if (value) {
    var regex = new RegExp("^[0-9]+$");
    if (!regex.test(value)) {
      errdialog.value = true;
      errMessage.value = "数値およびカンマのみ入力してください";
      return;
    }
  }

  //カンマ付与
  await commmaMoney(value);
};

/**
 * ハウスカードイベント @change
 */
const onChangeHouseCard = async (): Promise<void> => {
  if (regularDeliveryCreateInput.house_card !== house_card_before.value) {
    house_card_change.value = true;
    if (regularDeliveryCreateInput.house_card == "") {
      overlay.value = false;
      errdialog.value = true;
      errMessage.value =
        "ハウスカード番号をクリアする場合、会員情報のハウスカード番号もクリアされます。";
      return;
    }
  } else {
    house_card_change.value = false;
  }
};

/**
 * 初期処理
 */
const initialize = async (): Promise<void> => {
  regularDeliveryCreateInput.chain_code = localStorageGetChainCode();
  regularDeliveryCreateInput.customer_code = localStorageGetCustomerCode();
  regularDeliveryCreateInput.shop_code = localStorageGetShopCode();
};

  /**
   * 前画面からデータ受け渡し
   */
const pref_name = ref(regularDeliveryStore.pref_name);
const zip_code = ref(regularDeliveryStore.zip_code);
const user_id = ref(regularDeliveryStore.user_id);
const pref_name_befor = ref("");
  /**
   * 店舗情報
   */
const shopData = ref<{ name: string } | null | undefined>(null);

  /**
   * 個口数リスト
   */
const unitItems = ref<Array<number>>([]);
  /**
   * 決済方法
   */
const paymentMethodItems = ref<(string | undefined)[]>([]);
const paymentRequiredFlg = ref(false); //支払金額必須フラグ
const btnNonMemberToggle = ref(0);
const userErrorMessage = ref("");
const errorPrefSelect = ref("");

  /**
   * 会員データ
   */
const memberData = ref<
  | {
      __typename?: "Member";
      address1: string;
      address2: string;
      address3?: string | null | undefined;
      name1: string;
      name2: string;
      name_kana1: string;
      name_kana2: string;
      zip_code: string;
      phone: string;
      shop_code?: string | null | undefined;
      house_card?: string | null | undefined;
      notes?: string | null | undefined;
    }
  | null
  | undefined
>(undefined);

  // 配達エリア名
const deliveryAreaName = ref("");
const prefSelectValue = ref("");

/**
 * 配達開始時間の選択ボックス
 */
const deliveryStartHourItem = ref<Array<number>>([]);
const deliveryStartHourValues = ref<number | null | undefined>(undefined);

/**
 * 配達開始時間の選択ボックス
 */
const deliveryStartMinitValues = ref<
  { label: string; deliveryStartMinit: number } | null | undefined
>(undefined);

// 配達希望時間
const deliveryId_0_Flg = ref(false);

// 配達便情報のヘッダー
const deliveryAvailabilityData = ref<DeliveryAvailabilityData[][]>([]);
const disp_deliveryAvailabilityData = ref<DeliveryAvailabilityData[][]>([]);
const displayDeliveryTime = ref(""); //便表示用
const addressFixed = ref(false);
const addressDialog = ref(false);
const errorZipCode = ref("");

// ハウスカード
const house_card_display = ref(0);
const house_card_validation = ref("");
const house_card_required = ref(0);
const house_card_change = ref(false);
const house_card_before = ref("");

/**
 * 配達時刻一覧 input
 */
const deliveryTimeListInput = reactive<DeliveryTimeListInput>({
  area_code: [],
  chain_code: localStorageGetChainCode(),
  customer_code: localStorageGetCustomerCode(),
  shop_code: localStorageGetShopCode(),
  limit: 10000,
  offset: 0,
});

  /**
   * 配達可能なエリア・便 input
   */
const deliverableAreaDeliveryIdInput = reactive<DeliverableAreaDeliveryIdInput>({
  chain_code: localStorageGetChainCode(),
  customer_code: localStorageGetCustomerCode(),
  shop_code: "",
  delivered_zip_code: "",
});

  //  配達依頼作成 logInput
const operationInput = reactive<OperationInput>({
  area_code: "10",
  detail: "操作詳細",
  display_category: "画面名",
  operation: "操作",
  user_id: localStorageGetUserId(),
});

  /**
   * エリアリスト
   */
const areaListItems = ref<
  (
    | {
        __typename?: "DeliveryArea" | undefined;
        area_code: string;
        area_name: string;
        area_name_kana: string;
      }
    | null
    | undefined
  )[]
    | null
    | undefined
>([]);

  /**
   * 配達曜日情報
   */
const deliveryIdSelectValues = ref<
  [
    {
      label: string;
      value: number;
    },
  ] | null | undefined
>(undefined);

  /**
   * 配達時間情報
   */
const listDeliveryTime = ref<
  (
    | {
        __typename?: "DeliveryTimeResponse";
        area_code: string;
        chain_code: string;
        shop_code: string;
        delivery_id: number;
        delivery_start_time: string;
        delivery_end_time: string;
        max_deliverable_unit?: number | null | undefined;
        delivery_cap_mon?: number | null | undefined;
        delivery_cap_tue?: number | null | undefined;
        delivery_cap_wed?: number | null | undefined;
        delivery_cap_thu?: number | null | undefined;
        delivery_cap_fri?: number | null | undefined;
        delivery_cap_sat?: number | null | undefined;
        delivery_cap_sun?: number | null | undefined;
        display_days_setting?: number | null | undefined;
      }
    | null
    | undefined
  )[]
    | null
    | undefined
>(undefined);
const deliverySelectValues = ref<{
  area_delivery_id: string;
  area_code: string;
  delivery_id: number;
  delivery_name: string;
  delivery_start_time: string;
  delivery_end_time: string;
  delivery_time: string;
} | null | undefined>(null);

  /**
   * 配達依頼作成日の選択ボックス
   */
const deliveryRequestCreateDateItem = [
  { label: "当日", delivery_request_create_date: 0 },
  { label: "前日", delivery_request_create_date: 1 },
  { label: "前々日", delivery_request_create_date: 2 },
];
const deliveryRequestCreateDateValues = ref<
  { label: string; delivery_request_create_date: number } | null | undefined
>({
  label: "当日",
  delivery_request_create_date: 0,
});

  /**
   * 配達依頼作成時間の選択ボックス
   */
const deliveryRequestCreateTimeItem = ref<Array<number>>([]);
const deliveryRequestCreateTimeValues = ref<number | null | undefined>(0);

// 配達便(便)データ
const listDeliveryId = ref<ListData[]>([]);

/**
 * 休日設定の選択ボックス
 */
const deliveryIdItems = ref<{ value: number; label: string }[]>([]);

////////////////////////////////////////////////////////////////////////////////
// 初期画面
////////////////////////////////////////////////////////////////////////////////
onMounted(async () => {
  try {
    overlay.value = true;

    // モード判定
    if (regularDeliveryStore.processFlag === 1) {
      updateMode.value = true;
    } else if (regularDeliveryStore.processFlag === 2) {
      deleteMode.value = true;
      // 登録確認ダイアログ 表示
      displayConfirmDialog.value = true;
    }
    //初期化処理
    await initialize(); // Adjust if you have a function for this
    //文言データを取得する
    Object.assign(
      wordList,
      await getWordList(
        localStorageGetChainCode(),
        REGULAR_DELIVERY_REGISTER_FILE_NAME,
        localStorageGetCustomerCode()
      )
    );
    wordListLoaded.value = true;

    //graphql接続
    const apolloClient = await generateApolloClient();

    deliveryIdItems.value = [{ label: "全曜日", value: 99 }, ...GeneralTypeList.week];
    //チェーン情報を取得する
    const result = await getChainInfo(
      localStorageGetCustomerCode(),
      localStorageGetChainCode()
    );
    if (result !== undefined && result !== null) {
      house_card_display.value = result.house_card_display
        ? result.house_card_display
        : 0;
      house_card_validation.value = result.house_card_validation
        ? result.house_card_validation
        : "";
      if (
        house_card_validation.value !== "" &&
        house_card_validation.value.indexOf("required") !== -1
      ) {
        house_card_required.value = 1;
      }
    }
    // 店舗情報取得
    try {
      const shop_data: ApolloQueryResult<GetShopQuery> =
        await apolloClient.query<GetShopQuery>({
          query: GetShopDocument,
          variables: {
            chain_code: localStorageGetChainCode(),
            customer_code: localStorageGetCustomerCode(),
            shop_code: localStorageGetShopCode(),
          },
          fetchPolicy: "no-cache",
        });
      shopData.value = shop_data!.data!.getShop!;
    } catch (err: unknown) {
      if (err instanceof Error) {
        err.message = "店舗情報の取得に失敗しました。";
        throw err;
      } else {
        throw new Error("店舗情報の取得に失敗しました。（Unknown error）");
      }
    }

    //決済方法の取得処理
    const paymentList = await getChainPaymentMethod(
      localStorageGetCustomerCode(),
      localStorageGetChainCode()
    );
    paymentMethodItems.value = paymentList!.map(
      (paymentList) => paymentList?.payment_method
    );
    if (paymentMethodItems.value.length == 1) {
      regularDeliveryCreateInput.payment_method = paymentMethodItems.value[0]!;
      await onChangePaymentMethod(regularDeliveryCreateInput.payment_method);
    }

    // 個口数
    try {
      const max_unit: ApolloQueryResult<GetChainQuery> =
        await apolloClient.query<GetChainQuery>({
          query: GetChainDocument,
          variables: {
            chain_code: regularDeliveryCreateInput.chain_code,
            customer_code: regularDeliveryCreateInput.customer_code,
          },
          fetchPolicy: "no-cache",
        });

      for (let i = 0; i < Number(max_unit.data.getChain!.max_unit); i++) {
        unitItems.value.push(i + 1);
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        err.message = "個口数の取得に失敗しました。";
        throw err;
      } else {
        throw new Error("個口数の取得に失敗しました。（Unknown error）");
      }
    }
    //住所情報を取得する
    await initializeAddress();

    // 会員IDから情報取得する
    if (regularDeliveryStore.user_id !== "") {
      try {
        const member: ApolloQueryResult<GetMemberQuery> =
          await apolloClient.query<GetMemberQuery>({
            query: GetMemberDocument,
            variables: {
              logInput: operationInput,
              member_id: regularDeliveryStore.user_id,
            },
            fetchPolicy: "no-cache",
          });
        memberData.value = member.data.getMember;
        regularDeliveryCreateInput.delivery_address1 = memberData.value!.address1;
        regularDeliveryCreateInput.delivery_address2 = memberData.value!.address2;
        regularDeliveryCreateInput.delivery_address3 = memberData.value!.address3;
        regularDeliveryCreateInput.delivery_name1 = memberData.value!.name1;
        regularDeliveryCreateInput.delivery_name2 = memberData.value!.name2;
        regularDeliveryCreateInput.delivery_name_kana1 = memberData.value!.name_kana1;
        regularDeliveryCreateInput.delivery_name_kana2 = memberData.value!.name_kana2;
        regularDeliveryCreateInput.delivery_phone = memberData.value!.phone;
        regularDeliveryCreateInput.delivery_zip_code = memberData.value!.zip_code;
        regularDeliveryCreateInput.member_id = Number(regularDeliveryStore.user_id);
        regularDeliveryCreateInput.member_notes = memberData.value!.notes;
        regularDeliveryCreateInput.house_card = memberData.value!.house_card;
        house_card_before.value = regularDeliveryCreateInput.house_card!;
        house_card_change.value = house_card_before.value !== "";
      } catch (err: unknown) {
        if (err instanceof Error) {
          err.message = "会員情報の取得に失敗しました。";
          throw err;
        } else {
          throw new Error("会員情報の取得に失敗しました。（Unknown error）");
        }
      }
      originalAddress.value = regularDeliveryCreateInput.delivery_address1;
      deliveryTimeListInput.chain_code = localStorageGetChainCode();
      deliveryTimeListInput.shop_code = localStorageGetShopCode();
      // 配達エリア取得
      await GetAreaData(
        regularDeliveryCreateInput.delivery_address1,
        regularDeliveryCreateInput.delivery_zip_code
      );
      // 配達時間一覧
      await queryDeliverytime();
    }
    // 変更・削除時、値セット
    if (updateMode.value || deleteMode.value) {
      // 定期配送情報取得
      await getRegularDelivery(regularDeliveryStore.regular_delivery_no);
      // お届け便選択の曜日
      await SetdeliveryId();
      originalAddress.value = regularDeliveryCreateInput.delivery_address1;
      deliveryTimeListInput.chain_code = localStorageGetChainCode();
      deliveryTimeListInput.shop_code = localStorageGetShopCode();
      // 配達エリア取得
      await GetAreaData(
        regularDeliveryCreateInput.delivery_address1,
        regularDeliveryCreateInput.delivery_zip_code
      );
      // 配達時間一覧
      await queryDeliverytime();
      // お届け便名取得
      listDeliveryId.value!.forEach((value) => {
        if (
          value !== undefined &&
          value !== null &&
          value.delivery_id !== undefined &&
          value.delivery_id !== null
        ) {
          if (
            value.area_code == regularDeliveryCreateInput.area_code &&
            value.delivery_id == regularDeliveryCreateInput.delivery_id
          ) {
            deliverySelectValues.value = {
              area_delivery_id: value.area_code + String(value.delivery_id),
              area_code: value.area_code ? value.area_code : "",
              delivery_id: value.delivery_id,
              delivery_name: value.delivery_name ? value.delivery_name : "",
              delivery_start_time: value.delivery_start_time ? value.delivery_start_time : "",
              delivery_end_time: value.delivery_end_time ? value.delivery_end_time : "",
              delivery_time: value.delivery_time ? value.delivery_time : "",
            };
          }
        }
      });
      // 配達依頼作成日取得
      deliveryRequestCreateDateItem!.forEach((value) => {
        if (
          value !== undefined &&
          value !== null &&
          value.delivery_request_create_date !== undefined &&
          value.delivery_request_create_date !== null
        ) {
          if (
            value.delivery_request_create_date ==
            regularDeliveryCreateInput.delivery_request_create_date
          ) {
            deliveryRequestCreateDateValues.value = {
              delivery_request_create_date: value.delivery_request_create_date,
              label: value.label ? value.label : "",
            };
          }
        }
      });

      // 配達依頼作成時間作成
      await onChangeDeliveryRequestCreateDate();

      // 配達依頼作成時間取得
      if (regularDeliveryCreateInput.delivery_request_create_time) {
        deliveryRequestCreateTimeValues.value = Number(
          regularDeliveryCreateInput.delivery_request_create_time.substring(0, 2)
        );
      } else {
        deliveryRequestCreateTimeValues.value = 0;
      }
    }
  } catch (err: unknown) {
    errdialog.value = true;
    if (err instanceof Error) {
      errMessage.value = err.message;
    } else {
      errMessage.value = "予期しないエラーが発生しました。";
    }
    return;
  } finally {
    overlay.value = false;
  }
});

/**
 * 定期配送情報取得
 */
const getRegularDelivery = async (regular_delivery_no: string): Promise<void> => {
  try {
    const apolloClient = await generateApolloClient();

    // 定期配送一覧作成 logInput
    operationInput.detail = "定期配送";
    operationInput.display_category = "定期配送取得";

    const result = await apolloClient.query<
        GetRegularDeliveryQuery,
        GetRegularDeliveryQueryVariables
    >({
      query: GetRegularDeliveryDocument,
      variables: {
        regular_delivery_no,
        logInput: operationInput,
      },
      fetchPolicy: "no-cache",
    });

    if (
        result.data.getRegularDelivery !== undefined &&
        result.data.getRegularDelivery !== null
    ) {
      regularDeliveryCreateInput.area_code = result.data.getRegularDelivery.area_code!;
      regularDeliveryCreateInput.delivery_id = result.data.getRegularDelivery.delivery_id;
      regularDeliveryCreateInput.irregular_delivery_start_time =
          result.data.getRegularDelivery.irregular_delivery_start_time
            ? result.data.getRegularDelivery.irregular_delivery_start_time.substring(0, 5)
            : "";
      regularDeliveryCreateInput.irregular_delivery_end_time =
          result.data.getRegularDelivery.irregular_delivery_end_time
            ? result.data.getRegularDelivery.irregular_delivery_end_time.substring(0, 5)
            : "";
      regularDeliveryCreateInput.delivery_request_create_date =
          result.data.getRegularDelivery.delivery_request_create_date;
      regularDeliveryCreateInput.delivery_request_create_time =
          result.data.getRegularDelivery.delivery_request_create_time;
      regularDeliveryCreateInput.mon_to_deliver = result.data.getRegularDelivery.mon_to_deliver;
      regularDeliveryCreateInput.tue_to_deliver = result.data.getRegularDelivery.tue_to_deliver;
      regularDeliveryCreateInput.wed_to_deliver = result.data.getRegularDelivery.wed_to_deliver;
      regularDeliveryCreateInput.thu_to_deliver = result.data.getRegularDelivery.thu_to_deliver;
      regularDeliveryCreateInput.fri_to_deliver = result.data.getRegularDelivery.fri_to_deliver;
      regularDeliveryCreateInput.sat_to_deliver = result.data.getRegularDelivery.sat_to_deliver;
      regularDeliveryCreateInput.sun_to_deliver = result.data.getRegularDelivery.sun_to_deliver;
      regularDeliveryCreateInput.valid_date_from = result.data.getRegularDelivery.valid_date_from;
      regularDeliveryCreateInput.valid_date_to = result.data.getRegularDelivery.valid_date_to;
      regularDeliveryCreateInput.delivery_notes = result.data.getRegularDelivery.delivery_notes;

      regularDeliveryCreateInput.delivery_address1 = result.data.getRegularDelivery.delivery_address1;
      regularDeliveryCreateInput.delivery_address2 = result.data.getRegularDelivery.delivery_address2;
      regularDeliveryCreateInput.delivery_address3 = result.data.getRegularDelivery.delivery_address3;
      regularDeliveryCreateInput.delivery_name1 = result.data.getRegularDelivery.delivery_name1;
      regularDeliveryCreateInput.delivery_name2 = result.data.getRegularDelivery.delivery_name2;
      regularDeliveryCreateInput.delivery_name_kana1 = result.data.getRegularDelivery.delivery_name_kana1;
      regularDeliveryCreateInput.delivery_name_kana2 = result.data.getRegularDelivery.delivery_name_kana2;
      regularDeliveryCreateInput.delivery_phone = result.data.getRegularDelivery.delivery_phone;
      regularDeliveryCreateInput.delivery_zip_code = result.data.getRegularDelivery.delivery_zip_code;
      regularDeliveryCreateInput.member_id = result.data.getRegularDelivery.member_id;
      regularDeliveryCreateInput.payment_method = result.data.getRegularDelivery.payment_method;
      //金額が必須かどうか設定する
      await onChangePaymentMethod(result.data.getRegularDelivery.payment_method);

      dummy_payment_amount.value =
          result.data.getRegularDelivery.payment_amount !== null
              ? String(result.data.getRegularDelivery.payment_amount).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,")
              : "";

      regularDeliveryCreateInput.unit = result.data.getRegularDelivery.unit;
      regularDeliveryCreateInput.member_notes = result.data.getRegularDelivery.member_notes;
      regularDeliveryCreateInput.house_card = result.data.getRegularDelivery.house_card;

      house_card_before.value = regularDeliveryCreateInput.house_card!;
      if (house_card_before.value !== "") {
        house_card_change.value = true;
      } else {
        house_card_change.value = false;
      }
    }

    regularDeliveryUpdateInput.regular_delivery_no = regular_delivery_no;
  } catch (err: unknown) {
    if (err instanceof Error) {
      console.error(err);
      errMessage.value = err.message;
    } else {
      console.error("未知のエラー:", err);
      errMessage.value = "予期しないエラーが発生しました。";
    }
    errdialog.value = true;
    return undefined;
  }
};

/**
 * 住所情報を取得する
 */
const initializeAddress = async (): Promise<void> => {
  addressFixed.value = false;
  //graphql接続
  const apolloClient = await generateApolloClient();

  /**
   * 都道府県から住所を取得
   */
  originalAddress.value = pref_name.value;
  if (pref_name.value !== "") {
    cityBlockList.value = await getCityByPrefGroupedByStepA(
      localStorageGetCustomerCode(),
      localStorageGetChainCode(),
      localStorageGetShopCode(),
      pref_name.value
    );
    btnNonMemberToggle.value = 1;
  }

  /**
   * 郵便番号から住所を取得する
   */
  if (zip_code.value !== "") {
    btnNonMemberToggle.value = 0;
    originalAddress.value =
      pref_name.value + originalCityAddress.value + originalSectionAddress.value;

    // 郵便番号から丁目のリストを取得する
    try {
      const adddressBlock: ApolloQueryResult<GetBlockByZipCodeQuery> =
        await apolloClient.query<GetBlockByZipCodeQuery>({
          query: GetBlockByZipCodeDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: localStorageGetChainCode(),
            shop_code: localStorageGetShopCode(),
            zip_code: zip_code.value,
          },
          fetchPolicy: "no-cache",
        });

      cityBlockList.value = null;
      zipAddressList.value = adddressBlock.data.getBlockByZipCode;
      zipCodeFromPref.value = zip_code.value;

      if (!zipAddressList.value || zipAddressList.value.length == 0) {
        // 配達可能な住所がない場合は住所選択ダイアログにエラーを表示する
        errorZipCode.value = "入力した郵便番号はご利用対象外です";
        return;
      } else if (zipAddressList.value.length == 1) {
        //区画データが１つしかない
        if (
          zipAddressList.value[0] !== null &&
          zipAddressList.value[0] !== undefined
        ) {
          pref_name.value = zipAddressList.value[0]["pref_name"];
          originalCityAddress.value = zipAddressList.value[0][
            "city_name"
          ].replace("　", "");
          originalSectionAddress.value = zipAddressList.value[0][
            "section_name"
          ].replace("　", "");

          originalBlockName.value = "";
          if (
            zipAddressList.value[0]["block_name"] !== null &&
            zipAddressList.value[0]["block_name"] !== undefined
          ) {
            originalBlockName.value = zipAddressList.value[0][
              "block_name"
            ].replace("　", "");
          }
          regularDeliveryCreateInput.delivery_zip_code = zip_code.value;
          originalAddress.value =
            pref_name.value +
            originalCityAddress.value +
            originalSectionAddress.value;
          regularDeliveryCreateInput.delivery_address1 =
            originalAddress.value + originalBlockName.value;
          // 配達エリア取得
          await GetAreaData(
            regularDeliveryCreateInput.delivery_address1,
            regularDeliveryCreateInput.delivery_zip_code
          );

          // 配達時間一覧
          await queryDeliverytime();

          // エリア名取得
          await chkDeliveryId();
        }
        areaDialog.value = false;
        blockList.value = [];
        unassignedBlockList.value = [];
        clickMode.value = false;
        regularDeliveryCreateInput.delivery_zip_code = zip_code.value;
        addressFixed.value = true;
      } else {
        //複数の区画データがある
        let prev_city_name = "";
        let prev_section_name = "";
        let city_fixed = true;
        let section_fixed = true;

        for (let zipAddress of zipAddressList.value) {
          if (
            zipAddress !== null &&
            zipAddress !== undefined &&
            zipAddress["section_name"] !== null &&
            zipAddress["section_name"] !== undefined
          ) {
            if (prev_city_name == "") {
              prev_city_name = zipAddress["city_name"];
            } else if (prev_city_name != zipAddress["city_name"]) {
              city_fixed = false;
            }

            if (prev_section_name == "") {
              //最初のデータから都道府県をセットする
              pref_name.value = zipAddress["pref_name"];
              prev_section_name = zipAddress["section_name"];
            } else if (prev_section_name != zipAddress["section_name"]) {
              section_fixed = false;
            }
          }
        }

        if (city_fixed && section_fixed) {
          //大字・町名xが一意 -> ブロックリスト
          blockList.value = [];
          unassignedBlockList.value = [];
          for (let zipAddress of zipAddressList.value) {
            if (zipAddress !== null && zipAddress !== undefined) {
              // 空の場合は「その他」表示名を設定（master準拠）
              const blockName = zipAddress["block_name"];
              blockList.value.push(
                blockName && blockName.trim() !== ""
                  ? blockName
                  : wordList.lbl_other_block_disp_name
              );
            }
          }

          if (
            zipAddressList.value[0] !== null &&
            zipAddressList.value[0] !== undefined
          ) {
            prefSelectValue.value = zipAddressList.value[0]["pref_name"];
            originalCityAddress.value = zipAddressList.value[0][
              "city_name"
            ].replace("　", "");
            originalSectionAddress.value = zipAddressList.value[0][
              "section_name"
            ].replace("　", "");

            //指定した郵便番号内に配達対象外区画の丁目のリストが存在したらセットする（master準拠）
            await setDeliveryAreaSectionUnassigned(zip_code.value);
          }
        } else if (city_fixed) {
          //大字・町名が一意でない -> セクションリスト
          sectionBlockList.value = null;

          if (
            zipAddressList.value[0] !== null &&
            zipAddressList.value[0] !== undefined
          ) {
            originalCityAddress.value = zipAddressList.value[0][
              "city_name"
            ].replace("　", "");
          }

          sectionBlockList.value = {
            line_a: [],
            line_ka: [],
            line_sa: [],
            line_ta: [],
            line_na: [],
            line_ha: [],
            line_ma: [],
            line_ya: [],
            line_ra: [],
            line_wa: [],
            line_other: [],
          };
          let prev_name = "";
          for (let zipAddress of zipAddressList.value) {
            if (zipAddress !== null && zipAddress !== undefined) {
              let kana_step_a = zipAddress["section_kana"].substring(0, 1);

              if (prev_name != zipAddress["section_name"]) {
                prev_name = zipAddress["section_name"];
                if (
                  kana_step_a == "ｱ" ||
                  kana_step_a == "ｲ" ||
                  kana_step_a == "ｳ" ||
                  kana_step_a == "ｴ" ||
                  kana_step_a == "ｵ"
                ) {
                  if (
                    sectionBlockList.value.line_a !== null &&
                    sectionBlockList.value.line_a !== undefined
                  ) {
                    sectionBlockList.value.line_a.push(
                      zipAddress["section_name"].replace("　", "")
                    );
                  }
                } else if (
                  kana_step_a == "ｶ" ||
                  kana_step_a == "ｷ" ||
                  kana_step_a == "ｸ" ||
                  kana_step_a == "ｹ" ||
                  kana_step_a == "ｺ"
                ) {
                  if (
                    sectionBlockList.value.line_ka !== null &&
                    sectionBlockList.value.line_ka !== undefined
                  ) {
                    sectionBlockList.value.line_ka.push(
                      zipAddress["section_name"].replace("　", "")
                    );
                  }
                } else if (
                  kana_step_a == "ｻ" ||
                  kana_step_a == "ｼ" ||
                  kana_step_a == "ｽ" ||
                  kana_step_a == "ｾ" ||
                  kana_step_a == "ｿ"
                ) {
                  if (
                    sectionBlockList.value.line_sa !== null &&
                    sectionBlockList.value.line_sa !== undefined
                  ) {
                    sectionBlockList.value.line_sa.push(
                      zipAddress["section_name"].replace("　", "")
                    );
                  }
                } else if (
                  kana_step_a == "ﾀ" ||
                  kana_step_a == "ﾁ" ||
                  kana_step_a == "ﾂ" ||
                  kana_step_a == "ﾃ" ||
                  kana_step_a == "ﾄ"
                ) {
                  if (
                    sectionBlockList.value.line_ta !== null &&
                    sectionBlockList.value.line_ta !== undefined
                  ) {
                    sectionBlockList.value.line_ta.push(
                      zipAddress["section_name"].replace("　", "")
                    );
                  }
                } else if (
                  kana_step_a == "ﾅ" ||
                  kana_step_a == "ﾆ" ||
                  kana_step_a == "ﾇ" ||
                  kana_step_a == "ﾈ" ||
                  kana_step_a == "ﾉ"
                ) {
                  if (
                    sectionBlockList.value.line_na !== null &&
                    sectionBlockList.value.line_na !== undefined
                  ) {
                    sectionBlockList.value.line_na.push(
                      zipAddress["section_name"].replace("　", "")
                    );
                  }
                } else if (
                  kana_step_a == "ﾊ" ||
                  kana_step_a == "ﾋ" ||
                  kana_step_a == "ﾌ" ||
                  kana_step_a == "ﾍ" ||
                  kana_step_a == "ﾎ"
                ) {
                  if (
                    sectionBlockList.value.line_ha !== null &&
                    sectionBlockList.value.line_ha !== undefined
                  ) {
                    sectionBlockList.value.line_ha.push(
                      zipAddress["section_name"].replace("　", "")
                    );
                  }
                } else if (
                  kana_step_a == "ﾏ" ||
                  kana_step_a == "ﾐ" ||
                  kana_step_a == "ﾑ" ||
                  kana_step_a == "ﾒ" ||
                  kana_step_a == "ﾓ"
                ) {
                  if (
                    sectionBlockList.value.line_ma !== null &&
                    sectionBlockList.value.line_ma !== undefined
                  ) {
                    sectionBlockList.value.line_ma.push(
                      zipAddress["section_name"].replace("　", "")
                    );
                  }
                } else if (
                  kana_step_a == "ﾔ" ||
                  kana_step_a == "ﾕ" ||
                  kana_step_a == "ﾖ"
                ) {
                  if (
                    sectionBlockList.value.line_ya !== null &&
                    sectionBlockList.value.line_ya !== undefined
                  ) {
                    sectionBlockList.value.line_ya.push(
                      zipAddress["section_name"].replace("　", "")
                    );
                  }
                } else if (
                  kana_step_a == "ﾗ" ||
                  kana_step_a == "ﾘ" ||
                  kana_step_a == "ﾙ" ||
                  kana_step_a == "ﾚ" ||
                  kana_step_a == "ﾛ"
                ) {
                  if (
                    sectionBlockList.value.line_ra !== null &&
                    sectionBlockList.value.line_ra !== undefined
                  ) {
                    sectionBlockList.value.line_ra.push(
                      zipAddress["section_name"].replace("　", "")
                    );
                  }
                } else if (kana_step_a == "ﾜ") {
                  if (
                    sectionBlockList.value.line_wa !== null &&
                    sectionBlockList.value.line_wa !== undefined
                  ) {
                    sectionBlockList.value.line_wa.push(
                      zipAddress["section_name"].replace("　", "")
                    );
                  }
                }
              }
            }
          }
        } else {
          //市区郡町村が一意 -> シティリスト
          cityBlockList.value = null;

          cityBlockList.value = {
            line_a: [],
            line_ka: [],
            line_sa: [],
            line_ta: [],
            line_na: [],
            line_ha: [],
            line_ma: [],
            line_ya: [],
            line_ra: [],
            line_wa: [],
            line_other: [],
          };
          let prev_name = "";
          for (let zipAddress of zipAddressList.value) {
            if (zipAddress !== null && zipAddress !== undefined) {
              let kana_step_a = zipAddress["city_kana"].substring(0, 1);

              if (prev_name != zipAddress["city_name"]) {
                prev_name = zipAddress["city_name"];
                if (
                  kana_step_a == "ｱ" ||
                  kana_step_a == "ｲ" ||
                  kana_step_a == "ｳ" ||
                  kana_step_a == "ｴ" ||
                  kana_step_a == "ｵ"
                ) {
                  if (
                    cityBlockList.value.line_a !== null &&
                    cityBlockList.value.line_a !== undefined
                  ) {
                    cityBlockList.value.line_a.push(
                      zipAddress["city_name"].replace("　", "")
                    );
                  }
                } else if (
                  kana_step_a == "ｶ" ||
                  kana_step_a == "ｷ" ||
                  kana_step_a == "ｸ" ||
                  kana_step_a == "ｹ" ||
                  kana_step_a == "ｺ"
                ) {
                  if (
                    cityBlockList.value.line_ka !== null &&
                    cityBlockList.value.line_ka !== undefined
                  ) {
                    cityBlockList.value.line_ka.push(
                      zipAddress["city_name"].replace("　", "")
                    );
                  }
                } else if (
                  kana_step_a == "ｻ" ||
                  kana_step_a == "ｼ" ||
                  kana_step_a == "ｽ" ||
                  kana_step_a == "ｾ" ||
                  kana_step_a == "ｿ"
                ) {
                  if (
                    cityBlockList.value.line_sa !== null &&
                    cityBlockList.value.line_sa !== undefined
                  ) {
                    cityBlockList.value.line_sa.push(
                      zipAddress["city_name"].replace("　", "")
                    );
                  }
                } else if (
                  kana_step_a == "ﾀ" ||
                  kana_step_a == "ﾁ" ||
                  kana_step_a == "ﾂ" ||
                  kana_step_a == "ﾃ" ||
                  kana_step_a == "ﾄ"
                ) {
                  if (
                    cityBlockList.value.line_ta !== null &&
                    cityBlockList.value.line_ta !== undefined
                  ) {
                    cityBlockList.value.line_ta.push(
                      zipAddress["city_name"].replace("　", "")
                    );
                  }
                } else if (
                  kana_step_a == "ﾅ" ||
                  kana_step_a == "ﾆ" ||
                  kana_step_a == "ﾇ" ||
                  kana_step_a == "ﾈ" ||
                  kana_step_a == "ﾉ"
                ) {
                  if (
                    cityBlockList.value.line_na !== null &&
                    cityBlockList.value.line_na !== undefined
                  ) {
                    cityBlockList.value.line_na.push(
                      zipAddress["city_name"].replace("　", "")
                    );
                  }
                } else if (
                  kana_step_a == "ﾊ" ||
                  kana_step_a == "ﾋ" ||
                  kana_step_a == "ﾌ" ||
                  kana_step_a == "ﾍ" ||
                  kana_step_a == "ﾎ"
                ) {
                  if (
                    cityBlockList.value.line_ha !== null &&
                    cityBlockList.value.line_ha !== undefined
                  ) {
                    cityBlockList.value.line_ha.push(
                      zipAddress["city_name"].replace("　", "")
                    );
                  }
                } else if (
                  kana_step_a == "ﾏ" ||
                  kana_step_a == "ﾐ" ||
                  kana_step_a == "ﾑ" ||
                  kana_step_a == "ﾒ" ||
                  kana_step_a == "ﾓ"
                ) {
                  if (
                    cityBlockList.value.line_ma !== null &&
                    cityBlockList.value.line_ma !== undefined
                  ) {
                    cityBlockList.value.line_ma.push(
                      zipAddress["city_name"].replace("　", "")
                    );
                  }
                } else if (
                  kana_step_a == "ﾔ" ||
                  kana_step_a == "ﾕ" ||
                  kana_step_a == "ﾖ"
                ) {
                  if (
                    cityBlockList.value.line_ya !== null &&
                    cityBlockList.value.line_ya !== undefined
                  ) {
                    cityBlockList.value.line_ya.push(
                      zipAddress["city_name"].replace("　", "")
                    );
                  }
                } else if (
                  kana_step_a == "ﾗ" ||
                  kana_step_a == "ﾘ" ||
                  kana_step_a == "ﾙ" ||
                  kana_step_a == "ﾚ" ||
                  kana_step_a == "ﾛ"
                ) {
                  if (
                    cityBlockList.value.line_ra !== null &&
                    cityBlockList.value.line_ra !== undefined
                  ) {
                    cityBlockList.value.line_ra.push(
                      zipAddress["city_name"].replace("　", "")
                    );
                  }
                } else if (kana_step_a == "ﾜ") {
                  if (
                    cityBlockList.value.line_wa !== null &&
                    cityBlockList.value.line_wa !== undefined
                  ) {
                    cityBlockList.value.line_wa.push(
                      zipAddress["city_name"].replace("　", "")
                    );
                  }
                }
              }
            }
          }
        }
        areaDialog.value = true;
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        err.message = "丁目の取得に失敗しました。";
        throw err;
      } else {
        throw new Error("丁目の取得に失敗しました。（Unknown error）");
      }
    } finally {
      clickMode.value = false;
    }
  }
};

/**
 * 住所変更「検索する」ボタン @Click
 */
const onClickAddressChangeBtn = async (): Promise<void> => {
  if (clickMode.value) {
    return;
  }

  clickMode.value = true;
  try {
    addressFixed.value = false;
    // 郵便番号モードでは検索押下時に対象外チェックを再実行する。
    if (btnNonMemberToggle.value == 0) {
      await onZipCodeInput();
      if (errorZipCode.value) {
        return;
      }
    }

    //都道府県or郵便番号の選択により設定
    if (btnNonMemberToggle.value == 1) {
      //都道府県
      pref_name.value = prefSelectValue.value;
      zip_code.value = "";
    } else {
      //郵便番号
      zipCodeFromPref.value = zip_code.value;
      prefSelectValue.value = "";
    }

    //住所情報を取得する
    await initializeAddress();

    // 郵便番号検索でエラーが発生した場合は住所選択ダイアログに留まる
    if (errorZipCode.value) {
      return;
    }

    //住所選択を追加
    if (!addressFixed.value) {
      areaDialog.value = true;
    }
    //郵便番号・都道府県を閉じる
    addressDialog.value = false;
  } finally {
    clickMode.value = false;
  }
};

/**
 * 市区のリンクボタン選択時 @click
 */
const onClickCityBlockLink = async (item: string): Promise<void> => {
  if (clickMode.value == true) {
    return;
  } else {
    clickMode.value = true;
  }

  try {
    sectionBlockList.value = await getSectionByPrefCityGroupedByStepA(
      localStorageGetCustomerCode(),
      localStorageGetChainCode(),
      localStorageGetShopCode(),
      pref_name.value,
      item
    );
    originalCityAddress.value = item; //選択された市区町村
    blockList.value = [];
    unassignedBlockList.value = [];
  } catch (err: unknown) {
    console.log(err);
    errdialog.value = true;
    errMessage.value = "町村の取得に失敗しました。";

    //クリア
    await areaClear();
    return;
  } finally {
    clickMode.value = false;
  }
};

/**
 * 町村のリンクボタン選択時 @click
 */
const onClickSectionBlockLink = async (item: string): Promise<void> => {
  if (clickMode.value == true) {
    return;
  } else {
    clickMode.value = true;
  }

  try {
    //graphql接続
    const apolloClient = await generateApolloClient();

    if (item == "その他") {
      item = "";
    }

    // 郵便番号を取得する
    try {
      const getZipCode: ApolloQueryResult<GetZipCodeByPrefCitySectionQuery> =
        await apolloClient.query<GetZipCodeByPrefCitySectionQuery>({
          query: GetZipCodeByPrefCitySectionDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: localStorageGetChainCode(),
            shop_code: localStorageGetShopCode(),
            city: originalCityAddress.value,
            pref: pref_name.value,
            section: item,
          },
          fetchPolicy: "no-cache",
        });
      zipCodeFromPref.value = getZipCode.data.getZipCodeByPrefCitySection!;
      regularDeliveryCreateInput.delivery_zip_code =
        zipCodeFromPref.value;
    } catch (err: unknown) {
      if (err instanceof Error) {
        err.message = "該当する郵便番号が見つかりませんでした。";
        throw err;
      } else {
        throw new Error("該当する郵便番号が見つかりませんでした。");
      }
    }

    // 丁目のリストを取得する
    try {
      const Block: ApolloQueryResult<GetBlockByPrefCitySectionQuery> =
        await apolloClient.query<GetBlockByPrefCitySectionQuery>({
          query: GetBlockByPrefCitySectionDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: localStorageGetChainCode(),
            shop_code: localStorageGetShopCode(),
            city: originalCityAddress.value,
            pref: pref_name.value,
            section: item,
          },
          fetchPolicy: "no-cache",
        });
      // 空の場合は「その他」表示名を設定（master準拠）
      blockList.value = Block.data.getBlockByPrefCitySection!.map(
        (block: string | null | undefined) =>
          block && block.trim() !== ""
            ? block
            : wordList.lbl_other_block_disp_name
      );
      originalSectionAddress.value = item;
    } catch (err: unknown) {
      if (err instanceof Error) {
        err.message = "丁目の取得に失敗しました。";
        throw err;
      } else {
        throw new Error("丁目の取得に失敗しました。（Unknown error）");
      }
    }

    // 丁目がない場合は、モーダルを閉じて確定する（master準拠: 「その他」のみの場合も含む）
    if (
      blockList.value.length <= 0 ||
      (blockList.value.length == 1 && blockList.value[0] === wordList.lbl_other_block_disp_name)
    ) {
      blockList.value = [];
      unassignedBlockList.value = [];
      regularDeliveryCreateInput.delivery_address1 =
        pref_name.value +
        originalCityAddress.value +
        originalSectionAddress.value;
      regularDeliveryCreateInput.delivery_zip_code =
        zipCodeFromPref.value;
      // エリア情報を取得する
      await GetAreaData(
        regularDeliveryCreateInput.delivery_address1,
        regularDeliveryCreateInput.delivery_zip_code
      );
      // 配達時間一覧
      await queryDeliverytime();

      // エリア名取得
      await chkDeliveryId();

      // エリア選択モーダルを閉じる
      areaDialog.value = false;
      clickMode.value = false;
    }

    //指定した郵便番号内に配達対象外区画の丁目のリストが存在したらセットする（master準拠）
    await setDeliveryAreaSectionUnassigned(zipCodeFromPref.value);
  } catch (err: unknown) {
    if (err instanceof Error) {
      console.error(err);
      errMessage.value = err.message;
    } else {
      console.error("未知のエラー:", err);
      errMessage.value = "予期しないエラーが発生しました。";
    }
    errdialog.value = true;

    // クリア
    await areaClear();
    return;
  } finally {
    clickMode.value = false;
  }
};

//便選択色変更
const deliverySelectDayBox = ref<string>("-999");
const deliverySelectBox = ref<string>("-999");

/**
 * 配達対象外区画を取得する（master準拠）
 */
const setDeliveryAreaSectionUnassigned = async (zipCode: string): Promise<void> => {
  // 「その他」以外の丁目が存在しない場合は処理しない
  if (
    !blockList.value.some(
      (block) => block !== wordList.lbl_other_block_disp_name
    )
  ) {
    return;
  }

  try {
    const apolloClient = await generateApolloClient();
    const selectedAddress =
      pref_name.value + originalCityAddress.value + originalSectionAddress.value;

    const result: ApolloQueryResult<ListDeliveryAreaSectionUnassignedQuery> =
      await apolloClient.query<ListDeliveryAreaSectionUnassignedQuery>({
        query: ListDeliveryAreaSectionUnassignedDocument,
        variables: {
          input: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: localStorageGetChainCode(),
            shop_code: localStorageGetShopCode(),
            zip_code: zipCode,
          },
        },
        fetchPolicy: "no-cache",
      });

    if (
      result.data.listDeliveryAreaSectionUnassigned !== undefined &&
      result.data.listDeliveryAreaSectionUnassigned !== null
    ) {
      // 配達対象外区画を抽出してunassignedBlockListに設定
      const unassignedSections = result.data.listDeliveryAreaSectionUnassigned.map(
        (block) =>
          block?.delivery_area_section?.replace(selectedAddress, "") ||
          wordList.lbl_other_block_disp_name
      );
      unassignedBlockList.value = unassignedSections;
      // 配達対象外区画をブロック一覧に合流（master準拠）
      unassignedSections.forEach((section) => {
        if (!blockList.value.includes(section)) {
          blockList.value.push(section);
        }
      });
    }
  } catch (err: unknown) {
    if (err instanceof Error) {
      err.message = "配達対象外区画の取得に失敗しました。";
      throw err;
    } else {
      throw new Error("配達対象外区画の取得に失敗しました。（Unknown error）");
    }
  }
};

/**
 * エリア選択クリア処理
 */
const areaClear = async (): Promise<void> => {
  regularDeliveryCreateInput.area_code = "";
  deliveryAreaName.value = "";
  deliveryAvailabilityData.value = [];
  disp_deliveryAvailabilityData.value = [];
  sectionBlockList.value = null;
  blockList.value = [];
  unassignedBlockList.value = [];
  originalCityAddress.value = "";
  originalSectionAddress.value = "";
  regularDeliveryCreateInput.delivery_address1 =
    originalAddress.value + originalBlockName.value;
  regularDeliveryCreateInput.delivery_zip_code = "";
  deliveryId_0_Flg.value = false;
  deliveryStartHourItem.value = [];
  deliveryStartHourValues.value = undefined;
  deliveryStartMinitValues.value = undefined;
  displayDeliveryTime.value = "";

  deliverySelectDayBox.value = "-999";
  deliverySelectBox.value = "-999";
};

/**
 * 既存会員からの住所選択クリア処理
 */
const areaAddresSelectClear = async (): Promise<void> => {
  pref_name.value = "";
  prefSelectValue.value = "";
  cityBlockList.value = null;
  sectionBlockList.value = null;
  blockList.value = [];
  unassignedBlockList.value = [];
  addressBlockList.value = [];
  originalCityAddress.value = "";
  originalSectionAddress.value = "";
  originalBlockName.value = "";
  listDeliveryId.value = [];
  deliverySelectValues.value = null;
  areaListItems.value = [];
  deliveryTimeListInput.area_code = [];
};

/**
 * エリア情報取得
 */
async function GetAreaData(address: string, zip_code: string) {
  if (address == "" || zip_code == "") {
    return;
  }

  try {
    //graphql接続
    const apolloClient = await generateApolloClient();

    // エリアデータ取得処理
    const listArea: ApolloQueryResult<ListDeliveryAreaQuery> =
      await apolloClient.query<
        ListDeliveryAreaQuery,
        ListDeliveryAreaQueryVariables
      >({
        query: ListDeliveryAreaDocument,
        variables: {
          chain_code: localStorageGetChainCode(),
          customer_code: localStorageGetCustomerCode(),
          shop_code: localStorageGetShopCode(),
          address: address,
        },
        fetchPolicy: "no-cache",
      });
    if (
      listArea.data.listDeliveryArea !== null &&
      listArea.data.listDeliveryArea != undefined
    ) {
      listArea.data.listDeliveryArea.forEach((value) => {
        if (
          value !== undefined &&
          value !== null &&
          areaListItems.value !== null &&
          areaListItems.value !== undefined
        ) {
          areaListItems.value.push(value);
          deliveryTimeListInput.area_code!.push(value.area_code);
        }
      });
    }
  } catch (err: unknown) {
    if (err instanceof Error) {
      console.error(err);
    } else {
      console.error("未知のエラー:", err);
    }
    errdialog.value = true;
    errMessage.value = "配達エリアが存在しません。";
    return;
  }
}

//  配達依頼作成 input
const regularDeliveryCreateInput = reactive<RegularDeliveryCreateInput>({
  chain_code: localStorageGetChainCode(),
  customer_code: localStorageGetCustomerCode(),
  shop_code: localStorageGetShopCode(),
  area_code: "",
  delivery_id: 0,
  member_id: 0,
  delivery_notes: "",
  delivery_request_create_date: 0,
  delivery_request_create_time: "",
  irregular_delivery_end_time: "",
  irregular_delivery_start_time: "",
  mon_to_deliver: 0,
  tue_to_deliver: 0,
  wed_to_deliver: 0,
  thu_to_deliver: 0,
  fri_to_deliver: 0,
  sat_to_deliver: 0,
  sun_to_deliver: 0,
  valid_date_from: "",
  valid_date_to: "",
  delivery_address1: pref_name.value,
  delivery_address2: "",
  delivery_address3: "",
  delivery_name1: "",
  delivery_name2: "",
  delivery_name_kana1: "",
  delivery_name_kana2: "",
  delivery_phone: "",
  delivery_zip_code: zip_code.value,
  member_notes: "",
  payment_amount: null,
  payment_method: "",
  unit: 1,
  house_card: "",
  user_id: localStorageGetUserId(),
});

/**
 * 定期配送 input
 */
const regularDeliveryUpdateInput = reactive<RegularDeliveryUpdateInput>({
  regular_delivery_no: "",
  chain_code: localStorageGetChainCode(),
  customer_code: localStorageGetCustomerCode(),
  shop_code: localStorageGetShopCode(),
  area_code: "",
  delivery_id: 0,
  delivery_notes: undefined,
  delivery_request_create_date: 0,
  delivery_request_create_time: "",
  irregular_delivery_end_time: "",
  irregular_delivery_start_time: "",
  mon_to_deliver: 0,
  tue_to_deliver: 0,
  wed_to_deliver: 0,
  thu_to_deliver: 0,
  fri_to_deliver: 0,
  sat_to_deliver: 0,
  sun_to_deliver: 0,
  valid_date_from: undefined,
  valid_date_to: undefined,
  member_id: 0,
  delivery_address1: pref_name.value,
  delivery_address2: "",
  delivery_address3: "",
  delivery_name1: "",
  delivery_name2: "",
  delivery_name_kana1: "",
  delivery_name_kana2: "",
  delivery_phone: "",
  delivery_zip_code: zip_code.value,
  member_notes: "",
  payment_amount: null,
  payment_method: "",
  unit: 1,
  house_card: "",
  user_id: localStorageGetUserId(),
});

/**
 * 配達時間一覧処理
 */
const queryDeliverytime = async () => {
  //graphql接続
  const apolloClient = await generateApolloClient();
  listDeliveryId.value = [];

  // 配達時間一覧取得 [引数:input参照]
  let deliveryTime: ApolloQueryResult<ListDeliveryTimeQuery>;
  try {
    deliveryTime = await apolloClient.query<
      ListDeliveryTimeQuery,
      ListDeliveryTimeQueryVariables
    >({
      query: ListDeliveryTimeDocument,
      variables: {
        input: deliveryTimeListInput,
      },
      fetchPolicy: "no-cache",
    });
  } catch (err) {
    console.error(err);
    return;
  }

  if (deliveryTime.data.listDeliveryTime!.length > 0) {
    listDeliveryTime.value = deliveryTime.data.listDeliveryTime!;
    const deliveryIdKeySet = new Set<string>();

    //配達時間一覧作成
    listDeliveryTime.value!.forEach((value) => {
      if (value !== undefined && value !== null) {
        areaListItems.value!.forEach((area) => {
          if (
            area !== undefined &&
            area !== null &&
            area.area_code === value.area_code
          ) {
            const key = `${area.area_code}:99`;
            if (deliveryIdKeySet.has(key)) {
              return;
            }
            deliveryIdKeySet.add(key);
            listDeliveryId.value.push({
              area_delivery_id: area.area_code + "99",
              area_code: area.area_code,
              delivery_id: 99,
              delivery_name: area.area_name + "　定期便",
              delivery_start_time: "",
              delivery_end_time: "",
              delivery_time: "",
            });
          }
        });
      }
    });

    let area_name = "";
    listDeliveryTime.value!.forEach((value) => {
      if (value !== undefined && value !== null) {
        if (value.delivery_id !== 0) {
          // 都度便以外
          area_name = "";
          areaListItems.value!.forEach((area) => {
            if (
              area !== undefined &&
              area !== null &&
              area.area_code === value.area_code
            ) {
              area_name = area.area_name;
            }
          });
          const key = `${value.area_code}:${value.delivery_id}`;
          if (deliveryIdKeySet.has(key)) {
            return;
          }
          deliveryIdKeySet.add(key);
          listDeliveryId.value.push({
            area_delivery_id: value.area_code + String(value.delivery_id),
            area_code: value.area_code,
            delivery_id: value.delivery_id,
            delivery_name:
              area_name + "　" + String(value.delivery_id) + "便",
            delivery_start_time: value.delivery_start_time?.substring(0, 5),
            delivery_end_time: value.delivery_end_time?.substring(0, 5),
            delivery_time:
              value.delivery_start_time?.substring(0, 5) +
              "~" +
              value.delivery_end_time?.substring(0, 5),
          });
        }
      }
    });
  }
};

/**
 * 郵便番号選択 @Click
 */
const onClickToggleZip = async (): Promise<void> => {
  errorZipCode.value = "";
  userErrorMessage.value = "";
  errorPrefSelect.value = "";
  btnNonMemberToggle.value = 0;
};

/**
 * 都道府県選択 @Click
 */
const onClickTogglePref = async (): Promise<void> => {
  errorZipCode.value = "";
  userErrorMessage.value = "";
  errorPrefSelect.value = "";
  btnNonMemberToggle.value = 1;
};

/**
 * 郵便番号入力イベント @input
 */
const onZipCodeInput = async (): Promise<void> => {
  //graphql接続
  const apolloClient = await generateApolloClient();

  errorZipCode.value = "";
  userErrorMessage.value = "";
  errorPrefSelect.value = "";

  // 7桁の場合検索する、それ以外の場合住所をクリアする
  if (zip_code.value.length != 7) {
    return;
  }
  // 郵便番号から丁目のリストを取得する
  const adddressBlock: ApolloQueryResult<GetBlockByZipCodeQuery> =
    await apolloClient.query<GetBlockByZipCodeQuery>({
      query: GetBlockByZipCodeDocument,
      variables: {
        customer_code: localStorageGetCustomerCode(),
        chain_code: localStorageGetChainCode(),
        shop_code: localStorageGetShopCode(),
        zip_code: zip_code.value,
      },
      fetchPolicy: "no-cache",
    });

  if (
    !adddressBlock.data.getBlockByZipCode ||
    adddressBlock.data.getBlockByZipCode.length == 0
  ) {
    errorZipCode.value = "入力した郵便番号はご利用対象外です";
    return;
  }
};

/**
 * 都道府県入力イベント @input
 */
const onPrefSelectInput = async (): Promise<void> => {
  errorZipCode.value = "";
  userErrorMessage.value = "";
  errorPrefSelect.value = "";
};


/**
 * 配達便存在チェック
 */
const chkDeliveryId = async () => {
  try {
    //graphql接続
    const apolloClient = await generateApolloClient();

    deliverableAreaDeliveryIdInput.customer_code =
      regularDeliveryCreateInput.customer_code;
    deliverableAreaDeliveryIdInput.chain_code =
      regularDeliveryCreateInput.chain_code;
    deliverableAreaDeliveryIdInput.shop_code =
      regularDeliveryCreateInput.shop_code;

    // 配達便が存在しているかチェック
    const deliverableAreaDeliveryIdResult: ApolloQueryResult<ListDeliverableAreaDeliveryIdQuery> =
      await apolloClient.query<ListDeliverableAreaDeliveryIdQuery>({
        query: ListDeliverableAreaDeliveryIdDocument,
        variables: {
          input: deliverableAreaDeliveryIdInput,
        },
      });

    //存在していない場合エラー
    deliverableAreaDeliveryIdResult.data.listDeliverableAreaDeliveryId?.forEach(
      (value) => {
        if (value !== undefined && value !== null) {
          deliveryTimeListInput.area_code!.push(value.area_code);
        }
      }
    );
  } catch (err: unknown) {
    if (err instanceof Error) {
      console.error(err);
      errMessage.value = err.message;
    } else {
      console.error("未知のエラー:", err);
      errMessage.value = "予期しないエラーが発生しました。";
    }
    errdialog.value = true;
    return false;
  }
};

/**
 * お届け日を設定する
 */
const SetdeliveryId = async (): Promise<void> => {
  deliveryIdSelectValues.value = [];
  if (regularDeliveryCreateInput.mon_to_deliver !== 0) {
    deliveryIdSelectValues.value!.push(
      GeneralTypeList.week.find((element) => element.value === 1)!
    );
  }
  if (regularDeliveryCreateInput.tue_to_deliver !== 0) {
    deliveryIdSelectValues.value!.push(
      GeneralTypeList.week.find((element) => element.value === 2)!
    );
  }
  if (regularDeliveryCreateInput.wed_to_deliver !== 0) {
    deliveryIdSelectValues.value!.push(
      GeneralTypeList.week.find((element) => element.value === 3)!
    );
  }
  if (regularDeliveryCreateInput.thu_to_deliver !== 0) {
    deliveryIdSelectValues.value!.push(
      GeneralTypeList.week.find((element) => element.value === 4)!
    );
  }
  if (regularDeliveryCreateInput.fri_to_deliver !== 0) {
    deliveryIdSelectValues.value!.push(
      GeneralTypeList.week.find((element) => element.value === 5)!
    );
  }
  if (regularDeliveryCreateInput.sat_to_deliver !== 0) {
    deliveryIdSelectValues.value!.push(
      GeneralTypeList.week.find((element) => element.value === 6)!
    );
  }
  if (regularDeliveryCreateInput.sun_to_deliver !== 0) {
    deliveryIdSelectValues.value!.push(
      GeneralTypeList.week.find((element) => element.value === 7)!
    );
  }
};


const {
  delivery_time_input,
  onChangeDeliveryIdSelect,
  onChangeDeliveryId,
  onChangeDeliveryStartTime,
  onChangeDeliveryEndTime,
  onChangeDeliveryRequestCreateDate,
  formatDateYmd,
  onChangeValidDateFrom,
  onChangeValidDateTo,
  onClickAddressUpdate,
  clickMode,
  onClickAddressBlockLink,
  onClickAreaDialogReturn,
  getAreaList,
  onChangePaymentMethod,
} = useRegularDeliveryRegisterActions({
  deliveryIdSelectValues,
  deliverySelectValues,
  deliveryRequestCreateTimeItem,
  regularDeliveryCreateInput,
  errdialog,
  errMessage,
  deliveryRequestCreateDateValues,
  deliveryRequestCreateTimeValues,
  pref_name_befor,
  pref_name,
  prefSelectValue,
  areaAddresSelectClear,
  prefListItems,
  addressDialog,
  zipCodeFromPref,
  originalCityAddress,
  originalSectionAddress,
  deliveryTimeListInput,
  originalBlockName,
  originalAddress,
  queryDeliverytime,
  chkDeliveryId,
  areaClear,
  areaDialog,
  blockList,
  unassignedBlockList,
  btnNonMemberToggle,
  sectionBlockList,
  paymentRequiredFlg,
  wordList,
  areaListItems,
  listDeliveryId,
  GetAreaData,
});

const {
  onClickOpenConfirmDialog,
  onClickInputCancel,
  onClickBack,
  createRegularDeliveryMutation,
  updateRegularDeliveryMutation,
  deleteRegularDeliveryMutation,
  createDeliveryId,
  onClickConfirmEntry,
  onClickDelete,
  onClickConfirmCancel,
  createRegularDelivery,
  updateRegularDelivery,
  deleteRegularDelivery,
  copyCreateToUpdate,
  createMember,
  updateMember,
  onClickCompletedGoStart,
  allClear,
} = useRegularDeliveryRegisterSubmit({
  displayConfirmDialog,
  displayCompletedDialog,
  router,
  deleteMode,
  overlay,
  deliveryIdSelectValues,
  regularDeliveryCreateInput,
  deliverySelectValues,
  deliveryRequestCreateTimeValues,
  updateMode,
  dummy_payment_amount,
  regularDeliveryUpdateInput,
  operationInput,
  errdialog,
  errMessage,
  memberCreateInput,
  memberUpdateInput,
  createMemberMutation,
  updateMemberMutation,
  deliveryRequestCreateDateValues,
});

  return {
    router,
    errdialog,
    errMessage,
    updateMode,
    deleteMode,
    areaDialog,
    overlay,
    wordList,
    wordListLoaded,
    dummy_payment_amount,
    displayConfirmDialog,
    displayCompletedDialog,
    memberCreateInput,
    memberUpdateInput,
    createMemberMutation,
    updateMemberMutation,
    zipAddressList,
    zipCodeFromPref,
    cityBlockList,
    originalAddress,
    originalSectionAddress,
    originalCityAddress,
    originalBlockName,
    sectionBlockList,
    blockList,
    addressBlockList,
    unassignedBlockList,
    areaNotSelectLabel,
    prefListItems,
    commmaMoney,
    removeComma,
    onFocus,
    onBlur,
    onChangeHouseCard,
    initialize,
    pref_name,
    zip_code,
    user_id,
    pref_name_befor,
    shopData,
    unitItems,
    paymentMethodItems,
    paymentRequiredFlg,
    btnNonMemberToggle,
    userErrorMessage,
    errorPrefSelect,
    memberData,
    deliveryAreaName,
    prefSelectValue,
    deliveryStartHourItem,
    deliveryStartHourValues,
    deliveryStartMinitValues,
    deliveryId_0_Flg,
    deliveryAvailabilityData,
    disp_deliveryAvailabilityData,
    displayDeliveryTime,
    addressFixed,
    addressDialog,
    errorZipCode,
    house_card_display,
    house_card_validation,
    house_card_required,
    house_card_change,
    house_card_before,
    deliveryTimeListInput,
    deliverableAreaDeliveryIdInput,
    operationInput,
    areaListItems,
    deliveryIdSelectValues,
    listDeliveryTime,
    deliverySelectValues,
    deliveryRequestCreateDateItem,
    deliveryRequestCreateDateValues,
    deliveryRequestCreateTimeItem,
    deliveryRequestCreateTimeValues,
    listDeliveryId,
    deliveryIdItems,
    getRegularDelivery,
    initializeAddress,
    onClickAddressChangeBtn,
    onClickCityBlockLink,
    onClickSectionBlockLink,
    deliverySelectDayBox,
    deliverySelectBox,
    setDeliveryAreaSectionUnassigned,
    areaClear,
    areaAddresSelectClear,
    GetAreaData,
    regularDeliveryCreateInput,
    regularDeliveryUpdateInput,
    queryDeliverytime,
    onClickToggleZip,
    onClickTogglePref,
    onZipCodeInput,
    onPrefSelectInput,
    chkDeliveryId,
    SetdeliveryId,
    delivery_time_input,
    onChangeDeliveryIdSelect,
    onChangeDeliveryId,
    onChangeDeliveryStartTime,
    onChangeDeliveryEndTime,
    onChangeDeliveryRequestCreateDate,
    formatDateYmd,
    onChangeValidDateFrom,
    onChangeValidDateTo,
    onClickAddressUpdate,
    clickMode,
    onClickAddressBlockLink,
    onClickAreaDialogReturn,
    getAreaList,
    onChangePaymentMethod,
    onClickOpenConfirmDialog,
    onClickInputCancel,
    onClickBack,
    createRegularDeliveryMutation,
    updateRegularDeliveryMutation,
    deleteRegularDeliveryMutation,
    createDeliveryId,
    onClickConfirmEntry,
    onClickDelete,
    onClickConfirmCancel,
    createRegularDelivery,
    updateRegularDelivery,
    deleteRegularDelivery,
    copyCreateToUpdate,
    createMember,
    updateMember,
    onClickCompletedGoStart,
    allClear,
  };
};
